// src/pages/PurchasingGuidePage.jsx
import { Link } from "react-router-dom";
import "./PurchasingGuidePage.css";

const steps = [
  {
    num: "01",
    title: "Browse & Select Your Product",
    desc: "Start by browsing our product catalogue. Each product page shows all available options including size, quantity, stock type, coating, and turnaround time. Select the options that best suit your project.",
  },
  {
    num: "02",
    title: "Customise Your Options",
    desc: "Use the order form to configure your specifications. The price updates in real time as you change options. Once you're happy with your selection, click 'Add to Cart'.",
  },
  {
    num: "03",
    title: "Review Your Cart",
    desc: "In your cart you can review all selected products and options before checkout. Ensure quantities and specifications are correct before proceeding.",
  },
  {
    num: "04",
    title: "Enter Delivery Details",
    desc: "At checkout, enter your full delivery address. We ship across Canada and the United States via UPS. Select your province or state from the dropdown — the system will automatically fetch available shipping rates.",
  },
  {
    num: "05",
    title: "Choose a Shipping Method",
    desc: "After entering your address, available UPS shipping options will appear with their estimated delivery times and costs. Choose the option that best suits your deadline and budget.",
  },
  {
    num: "06",
    title: "Upload Your Artwork (PDF only)",
    desc: "Upload a print-ready PDF for each product in your order. Every item requires its own artwork file — if you have 3 products, you must upload 3 separate PDF files. Your PDF should be in CMYK colour mode at 300 DPI with 0.125 inch bleed on all sides.",
  },
  {
    num: "07",
    title: "Complete Payment",
    desc: "Enter your card details on the secure Stripe payment page. Your payment is processed securely — we never store your card details. Once payment is confirmed, your order is automatically forwarded to production.",
  },
  {
    num: "08",
    title: "Track Your Order",
    desc: "After placing your order, you can track its status from your account page. Statuses progress from Processing → In Production → Dispatched → Delivered. You will also receive a confirmation email.",
  },
];

const tips = [
  { icon: "📄", title: "Artwork must be PDF", desc: "We only accept print-ready PDF files. Other formats (JPG, PNG, AI) are not accepted at checkout." },
  { icon: "🎨", title: "Use CMYK colour mode", desc: "RGB colours will shift when printed. Always convert your artwork to CMYK before exporting to PDF." },
  { icon: "📐", title: "Include bleed", desc: "Add 0.125\" (3mm) bleed on all sides and keep important content 0.125\" inside the trim line." },
  { icon: "🖨️", title: "300 DPI minimum", desc: "Low-resolution artwork (under 300 DPI) will print blurry. Always export at full print resolution." },
  { icon: "📦", title: "One artwork per product", desc: "Each product in your cart requires its own PDF file. Uploading the same file for different products with different specs may cause print errors." },
  { icon: "⏱️", title: "Turnaround times", desc: "Turnaround starts after your artwork is approved, not from the time of payment. Rush options are available at checkout." },
];

export default function PurchasingGuidePage() {
  return (
    <div className="pg-page">
      {/* Hero */}
      <div className="pg-hero">
        <div className="container reveal">
          <span className="tag">How It Works</span>
          <h1>Purchasing Guide</h1>
          <p>Everything you need to know about ordering from Circuit Printing — from choosing your product to receiving your delivery.</p>
        </div>
      </div>

      <div className="container pg-body">
        {/* Steps */}
        <section className="pg-section">
          <h2>Step-by-Step Ordering Process</h2>
          <div className="pg-steps">
            {steps.map((s) => (
              <div key={s.num} className="pg-step reveal">
                <div className="pg-step-num">{s.num}</div>
                <div className="pg-step-body">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Tips */}
        <section className="pg-section">
          <h2>Artwork Tips &amp; Requirements</h2>
          <div className="pg-tips-grid">
            {tips.map((t) => (
              <div key={t.title} className="pg-tip reveal">
                <span className="pg-tip-icon">{t.icon}</span>
                <h4>{t.title}</h4>
                <p>{t.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="pg-cta reveal">
          <h2>Ready to place your order?</h2>
          <p>Browse our full range of print products and get an instant price.</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/products" className="btn-primary" style={{ padding: "14px 32px" }}>Browse Products →</Link>
            <Link to="/artwork-guidelines" className="btn-outline" style={{ padding: "14px 32px" }}>Artwork Guidelines</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
